import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, money2 } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  X,
  Loader2,
  ReceiptText,
  CheckCircle2,
  QrCode,
  ExternalLink,
  MessageCircle,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
const formatReceiptDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${day}-${month}-${year}, ${time}`;
};

export const MODES = ["Cash", "UPI", "Razorpay"];

export default function PaymentDialog({
  open,
  setOpen,
  total,
  grossTotal = total,
  buildBody,
  onDone,
  settings,
  restaurantName,
  restaurantAddress = "",
  restaurantPhone = "",
  restaurantTagline = "",
  existingOrder = null,
  receiptOnly = false,
}) {
  const [rows, setRows] = useState([{ mode: "Cash", amount: total }]);
  const [busy, setBusy] = useState(false);
  const [receiptOrder, setReceiptOrder] = useState(null);

  useEffect(() => {
    if (open && receiptOnly && existingOrder) {
      setReceiptOrder(existingOrder);
    }
  }, [open, receiptOnly, existingOrder]);

  const [upiId, setUpiId] = useState("");
  const [loadingUpi, setLoadingUpi] = useState(false);
  const [upiConfirmed, setUpiConfirmed] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");

  const discounts = settings?.payment_discounts || {};

  const availableModes = ["Cash"];

  if (settings?.upi_id?.trim()) {
    availableModes.push("UPI");
  }

  if (
    settings?.razorpay_enabled &&
    settings?.razorpay_key_id?.trim() &&
    settings?.razorpay_key_secret_configured
  ) {
    availableModes.push("Razorpay");
  }
  const primaryMode = rows[0]?.mode || "Cash";
  const pct = rows.length === 1 ? Number(discounts[primaryMode]) || 0 : 0;

  // Payment-method discount is calculated on the exact gross bill total.
  const discount =
    Math.round((((Number(grossTotal) || 0) * pct) / 100) * 100) / 100;

  // Payable amount after payment discount, before final round-off.
  const payable =
    Math.round(((Number(grossTotal) || 0) - discount) * 100) / 100;

  // Final customer payable amount.
  // Only the final payable amount is rounded to the nearest rupee.
  const due = Math.round(payable);

  useEffect(() => {
    if (open) {
      setRows([{ mode: "Cash", amount: total }]);
    }
  }, [open, total]);

  useEffect(() => {
    if (open && rows.length === 1) {
      setRows([{ mode: primaryMode, amount: due }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryMode, open]);

  const paid = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const remaining = due - paid;

  const receiptPaymentMode =
    receiptOrder?.payments?.length === 1
      ? receiptOrder.payments[0]?.mode
      : null;

  const receiptDiscountPct = Number(discounts[receiptPaymentMode]) || 0;

  const receiptPaymentDiscount =
    Math.round(
      (((Number(receiptOrder?.total) || 0) * receiptDiscountPct) / 100) * 100,
    ) / 100;

  const receiptFinalAmount = Math.round(
    (Number(receiptOrder?.total) || 0) - receiptPaymentDiscount,
  );

  const printWindowsThermalReceipt = (printData) => {
    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const money = (value) => Number(value || 0).toFixed(2);

    const itemsHtml = (printData.items || [])
      .map(
        (item) => `
      <div class="item-row">
        <span class="item-name">
          ${escapeHtml(item.name)}
        </span>

        <span class="item-amount">
          ${item.qty} × ₹${money(item.amount)}
        </span>
      </div>
    `,
      )
      .join("");

    const receiptHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />

        <title>Receipt</title>

        <style>
          @page {
            size: 58mm auto;
            margin: 0;
          }

          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            width: 58mm;
            background: #fff;
          }

          body {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #000;
  -webkit-font-smoothing: none;
}

          .receipt {
            width: 58mm;
            padding: 3mm 2.5mm;
          }

          .center {
            text-align: center;
          }

          .restaurant {
            font-size: 15px;
            font-weight: 700;
            margin-bottom: 3px;
          }

          .order {
            font-size: 11px;
            margin-bottom: 2px;
          }

          .date {
            font-size: 11px;
            margin-bottom: 6px;
          }

          .line {
            border-top: 1px dashed #000;
            margin: 5px 0;
          }

          .item {
            margin-bottom: 5px;
          }

         .item-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 5px;
}

.item-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  word-break: break-word;
}

.item-amount {
  font-size: 13px;
  white-space: nowrap;
  text-align: right;
}

          .summary-row {
  display: flex;
  justify-content: space-between;
  margin: 4px 0;
  font-size: 13px;
}

.final {
  font-size: 14px;
  font-weight: 700;
  margin-top: 5px;
}

          .gross {
            font-weight: 600;
          }

          

          .paid {
            font-weight: 600;
          }

          .footer {
            text-align: center;
            margin-top: 10px;
            font-size: 10px;
          }
        </style>
      </head>

      <body>
        <div class="receipt">

          
          <div class="center restaurant">
            ${escapeHtml(printData.restaurantName || "Restaurant")}
          </div>

          ${
            printData.restaurantAddress
              ? `<div class="center">${escapeHtml(printData.restaurantAddress)}</div>`
              : ""
          }

          ${
            printData.restaurantPhone
              ? `<div class="center">Phone: ${escapeHtml(printData.restaurantPhone)}</div>`
              : ""
          }

          <div class="center order">

            Order #${escapeHtml(printData.orderNo || "")}
          </div>

          <div class="center date">
            ${escapeHtml(printData.createdAt || "")}
          </div>

          <div class="line"></div>

          ${itemsHtml}

          <div class="line"></div>

          <div class="summary-row">
            <span>Subtotal</span>
            <span>₹${money(printData.subtotal)}</span>
          </div>

          ${
            Number(printData.discount || 0) > 0
              ? `
                <div class="summary-row">
                  <span>Discount</span>
                  <span>-₹${money(printData.discount)}</span>
                </div>
              `
              : ""
          }

          ${
            Number(printData.gst || 0) > 0
              ? `
                <div class="summary-row">
                  <span>GST</span>
                  <span>₹${money(printData.gst)}</span>
                </div>
              `
              : ""
          }

          <div class="summary-row gross">
            <span>Gross Total</span>
            <span>₹${money(printData.grossTotal)}</span>
          </div>

          ${
            Number(printData.paymentDiscount || 0) > 0
              ? `
                <div class="summary-row">
                  <span>Payment Discount</span>
                  <span>-₹${money(printData.paymentDiscount)}</span>
                </div>
              `
              : ""
          }

          <div class="summary-row final">
            <span>Final Amount</span>
            <span>₹${money(printData.finalAmount)}</span>
          </div>

          <div class="summary-row paid">
            <span>Paid</span>
            <span>₹${money(printData.paid)}</span>
          </div>

          <div class="line"></div>

          
          <div class="footer">
            Thank You!
            ${
              printData.restaurantTagline
                ? `<div style="margin-top:4px;">${escapeHtml(printData.restaurantTagline)}</div>`
                : ""
            }
          </div>


        </div>
      </body>
    </html>
  `;

    const printWindow = window.open("", "_blank", "width=400,height=600");

    if (!printWindow) {
      toast.error("Please allow popups to print the receipt.");
      return;
    }

    printWindow.document.open();
    printWindow.document.write(receiptHtml);
    printWindow.document.close();

    printWindow.focus();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 300);
    };

    printWindow.onafterprint = () => {
      printWindow.close();
    };
  };
  const printThermalReceipt = async () => {
    if (!receiptOrder) return;

    try {
      const items = receiptOrder.items || [];

      const printData = {
        restaurantName: restaurantName || "Restaurant",
        restaurantAddress,
        restaurantPhone,
        restaurantTagline,
        orderNo: receiptOrder.order_no || "",
        createdAt: formatReceiptDateTime(receiptOrder.created_at),

        items: items.map((item) => ({
          name: item.name || "",
          qty: Number(item.qty) || 0,
          amount: Number(item.price || 0) * Number(item.qty || 0),
        })),

        subtotal: Number(receiptOrder.subtotal || 0),
        discount: Number(receiptOrder.discount_total || 0),
        gst: Number(receiptOrder.tax_amount || 0),
        grossTotal: Number(receiptOrder.total || 0),
        paymentDiscount: Number(receiptPaymentDiscount || 0),
        finalAmount: Number(receiptFinalAmount || 0),
        paid: Number(receiptOrder.paid_amount || 0),
      };

      console.log("🖨️ PRINT DATA:", printData);

      const printUrl = `stayserveprinter://print?data=${encodeURIComponent(
        JSON.stringify(printData),
      )}`;

      console.log("🖨️ PRINT URL:", printUrl);

      console.log("🖨️ OPENING PRINTER APP...");

      //window.location.href = printUrl;

      const isAndroid = /Android/i.test(navigator.userAgent);

      if (isAndroid) {
        console.log("🖨️ Android → printer APK");
        console.log("🖨️ PRINT URL:", printUrl);

        window.location.href = printUrl;
      } else {
        console.log("🖨️ Windows/Desktop → thermal receipt print");

        printWindowsThermalReceipt(printData);
      }

      setReceiptOrder(null);
      setOpen(false);
    } catch (error) {
      console.error("Printer error:", error);

      toast.error(
        "Printer not connected. Please check the printer and try again.",
      );
    }
  };
  const shareBillOnWhatsApp = () => {
    console.log("🔥 WhatsApp Share button CLICKED");

    if (!receiptOrder) {
      console.log("❌ receiptOrder missing");
      return;
    }

    console.log("✅ receiptOrder:", receiptOrder);
    console.log("📱 whatsappNumber:", whatsappNumber);

    const digits = whatsappNumber.replace(/\D/g, "");

    console.log("🔢 digits:", digits);

    let mobile = digits;

    if (mobile.length === 12 && mobile.startsWith("91")) {
      mobile = mobile.slice(2);
    }

    console.log("📱 final mobile:", mobile);

    if (mobile.length !== 10) {
      console.log("❌ Invalid mobile number");
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    const items = receiptOrder.items || [];

    const itemLines = items
      .map(
        (item) => `${item.name} x${item.qty} - ${money(item.price * item.qty)}`,
      )
      .join("\n");

    const message = [
      `*${restaurantName || "Restaurant"}*`,
      restaurantAddress || null,
      restaurantPhone ? `Phone: ${restaurantPhone}` : null,
      "",
      `Order #${receiptOrder.order_no}`,
      `Date: ${formatReceiptDateTime(receiptOrder.created_at)}`,
      "",
      "*Order Details*",
      itemLines,
      "",
      `Subtotal: ${money(receiptOrder.subtotal || 0)}`,
      receiptOrder.discount_total > 0
        ? `Discount: -${money(receiptOrder.discount_total)}`
        : null,
      `GST: ${money2(receiptOrder.tax_amount || 0)}`,
      `Gross Total: ${money2(receiptOrder.total || 0)}`,
      receiptPaymentDiscount > 0
        ? `${receiptPaymentMode} Discount (${receiptDiscountPct}%): -${money2(
            receiptPaymentDiscount,
          )}`
        : null,
      `*Final Amount: ${money(receiptFinalAmount)}*`,
      `Paid: ${money(receiptOrder.paid_amount || 0)}`,
      "",
      "Thank You! 🙏",
      restaurantTagline || null,
    ]
      .filter(Boolean)
      .join("\n");

    const whatsappUrl = `https://wa.me/91${mobile}?text=${encodeURIComponent(
      message,
    )}`;

    const whatsappWindow = window.open(whatsappUrl, "_blank");

    if (!whatsappWindow) {
      window.location.href = whatsappUrl;
      return;
    }

    setWhatsappOpen(false);
    setWhatsappNumber("");
  };

  const loadRazorpay = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Razorpay SDK failed to load"));

      document.body.appendChild(script);
    });

  const confirm = async () => {
    setBusy(true);

    try {
      // ---------------------------------------------------------
      // Existing order
      // Basic dine-in order already exists.
      // Payment is made against the existing order.
      // ---------------------------------------------------------
      if (existingOrder) {
        const createdOrder = existingOrder;

        if (!createdOrder?.id) {
          throw new Error("Existing order ID is missing");
        }

        const serverTotal = Number(createdOrder.total || 0);

        if (serverTotal <= 0) {
          throw new Error("Invalid order total");
        }

        let paymentRows;

        if (rows.length === 1) {
          paymentRows = [
            {
              mode: rows[0].mode,
              amount: due,
            },
          ];
        } else {
          paymentRows = rows.map((r) => ({
            mode: r.mode,
            amount: Number(r.amount) || 0,
          }));
        }

        const paidAmount = Number(
          paymentRows
            .reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
            .toFixed(2),
        );

        if (Math.abs(paidAmount - due) > 0.01) {
          toast.error(
            `Payment amount ${money(paidAmount)} does not match amount due ${money(due)}`,
          );
          return;
        }

        // Existing order → use normal pay endpoint
        // ---------------------------------------------------------
        // Existing order → Razorpay payment flow
        // ---------------------------------------------------------
        if (primaryMode === "Razorpay") {
          await loadRazorpay();

          // 1. Create Razorpay order for the existing restaurant order
          const razorpayOrderResponse = await api.createRazorpayOrder(
            createdOrder.id,
          );

          const razorpayData = razorpayOrderResponse.data;

          if (!razorpayData?.razorpay_order_id || !razorpayData?.key_id) {
            throw new Error("Razorpay order could not be created");
          }

          // 2. Open Razorpay Checkout
          await new Promise((resolve, reject) => {
            const options = {
              key: razorpayData.key_id,

              amount: razorpayData.amount,
              currency: razorpayData.currency || "INR",

              name: restaurantName || "Restaurant",
              description: `Order #${createdOrder.order_no}`,

              order_id: razorpayData.razorpay_order_id,

              handler: async function (response) {
                try {
                  // 3. Verify Razorpay payment FIRST
                  await api.verifyRazorpayPayment(createdOrder.id, {
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                  });

                  // 4. ONLY after verification → mark order as paid
                  const paidOrder = await api.payOrder(createdOrder.id, [
                    {
                      mode: "Razorpay",
                      amount: due,
                    },
                  ]);

                  const finalizedOrder = paidOrder.data;

                  if (!finalizedOrder) {
                    throw new Error(
                      "Payment completed but order data was not returned",
                    );
                  }

                  if (finalizedOrder.approval_status !== "approved") {
                    throw new Error(
                      "Payment completed but the order was not approved. Please refresh and try again.",
                    );
                  }

                  setReceiptOrder(finalizedOrder);

                  toast.success(
                    `Bill generated · Order #${finalizedOrder.order_no} paid`,
                  );

                  setOpen(false);
                  onDone();

                  resolve();
                } catch (error) {
                  reject(error);
                }
              },

              modal: {
                ondismiss: function () {
                  reject(new Error("Payment cancelled"));
                },
              },

              theme: {
                color: "#f97316",
              },
            };

            const razorpay = new window.Razorpay(options);

            razorpay.on("payment.failed", function (response) {
              reject(
                new Error(
                  response?.error?.description || "Razorpay payment failed",
                ),
              );
            });

            razorpay.open();
          });

          return;
        }

        // ---------------------------------------------------------
        // Existing order → Cash / UPI normal payment
        // ---------------------------------------------------------
        const paidOrder = await api.payOrder(createdOrder.id, paymentRows);

        const finalizedOrder = paidOrder.data;

        if (!finalizedOrder) {
          throw new Error("Payment completed but order data was not returned");
        }

        if (finalizedOrder.approval_status !== "approved") {
          throw new Error(
            "Payment completed but the order was not approved. Please refresh and try again.",
          );
        }

        setReceiptOrder(finalizedOrder);

        toast.success(
          `Bill generated · Order #${finalizedOrder.order_no} paid and sent to kitchen`,
        );

        setOpen(false);
        onDone();

        return;
      }

      // ---------------------------------------------------------
      // NEW direct POS order
      // Cash / UPI → create paid order directly.
      // Razorpay → payment first, then create restaurant order.
      // ---------------------------------------------------------

      // Razorpay payment-first flow
      if (primaryMode === "Razorpay") {
        const razorpayAmount = Number(due);

        if (!razorpayAmount || razorpayAmount <= 0) {
          throw new Error("Invalid Razorpay payment amount");
        }

        // 1. Create Razorpay order BEFORE creating restaurant order
        const razorpayOrderResponse = await api.createRazorpayPaymentOrder({
          amount: razorpayAmount,
        });

        const razorpayData = razorpayOrderResponse.data;

        if (
          !razorpayData?.razorpay_order_id ||
          !razorpayData?.payment_intent_id ||
          !razorpayData?.key_id
        ) {
          throw new Error("Razorpay order could not be created");
        }

        // Razorpay Checkout must be available
        await loadRazorpay();

        await new Promise((resolve, reject) => {
          const options = {
            key: razorpayData.key_id,

            amount: razorpayData.amount,
            currency: razorpayData.currency || "INR",

            name: restaurantName || "Restaurant",
            description: "Restaurant Order Payment",

            order_id: razorpayData.razorpay_order_id,

            handler: async function (response) {
              try {
                // 2. Verify payment with backend
                const verifyResponse = await api.verifyRazorpayPaymentFirst({
                  razorpay_order_id: response.razorpay_order_id,

                  razorpay_payment_id: response.razorpay_payment_id,

                  razorpay_signature: response.razorpay_signature,
                });

                const verifyData = verifyResponse.data;

                if (!verifyData?.verified) {
                  throw new Error("Razorpay payment verification failed");
                }

                // 3. ONLY after successful verification,
                // create the actual restaurant order.
                const paymentRows = [
                  {
                    mode: "Razorpay",
                    amount: razorpayAmount,
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    payment_intent_id:
                      verifyData.payment_intent_id ||
                      razorpayData.payment_intent_id,
                  },
                ];

                const orderBody = {
                  ...buildBody(),
                  payments: paymentRows,
                };

                const order = await api.createOrder(orderBody);

                const finalizedOrder = order.data;

                if (!finalizedOrder?.id) {
                  throw new Error(
                    "Payment succeeded but order could not be created",
                  );
                }

                if (finalizedOrder.payment_status !== "paid") {
                  throw new Error(
                    "Payment succeeded but order was not marked as paid",
                  );
                }

                setReceiptOrder(finalizedOrder);

                toast.success(
                  `Bill generated · Order #${finalizedOrder.order_no} paid`,
                );

                setOpen(false);
                onDone();

                resolve();
              } catch (error) {
                reject(error);
              }
            },

            modal: {
              ondismiss: function () {
                reject(new Error("Payment cancelled"));
              },
            },

            theme: {
              color: "#f97316",
            },
          };

          const razorpay = new window.Razorpay(options);

          razorpay.on("payment.failed", function (response) {
            reject(
              new Error(
                response?.error?.description || "Razorpay payment failed",
              ),
            );
          });

          razorpay.open();
        });

        return;
      }

      // ---------------------------------------------------------
      // Cash / UPI direct POS order
      // Existing flow remains unchanged.
      // ---------------------------------------------------------

      const paymentRows = rows.map((r) => ({
        mode: r.mode,
        amount: Number(r.amount) || 0,
      }));

      const orderBody = {
        ...buildBody(),
        payments: paymentRows,
      };

      const order = await api.createOrder(orderBody);

      const finalizedOrder = order.data;

      if (!finalizedOrder?.id) {
        throw new Error("Order was created but order ID is missing");
      }

      if (finalizedOrder.payment_status !== "paid") {
        throw new Error("Order was created but payment was not completed");
      }

      setReceiptOrder(finalizedOrder);

      toast.success(`Bill generated · Order #${finalizedOrder.order_no} paid`);

      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (primaryMode === "UPI") {
      setUpiId(settings?.upi_id || "");
      setUpiConfirmed(false);
    } else {
      setUpiId("");
      setUpiConfirmed(false);
    }
  }, [primaryMode, settings]);
  const upiAmount = Math.max(Number(remaining), 0).toFixed(2);

  const upiQrData = upiId
    ? `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(
        restaurantName,
      )}&am=${upiAmount}&cu=INR`
    : "";

  return (
    <>
      <Dialog open={open && !receiptOnly} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white w-[calc(100vw-24px)] max-w-[420px] max-h-[90vh] overflow-y-auto rounded-2xl"
          data-testid="payment-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">Collect Payment</DialogTitle>
          </DialogHeader>

          <div className="text-center py-2">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Amount due
            </p>

            <p
              className="font-display text-4xl font-bold text-primary"
              data-testid="pay-due"
            >
              {money2(grossTotal)}
            </p>

            {discount > 0 && (
              <p
                className="text-sm text-emerald-600 font-semibold mt-1"
                data-testid="pay-discount"
              >
                {primaryMode} discount ({pct}%) −{money2(discount)}
              </p>
            )}
          </div>

          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex gap-2">
                <select
                  value={r.mode}
                  onChange={(e) =>
                    setRows(
                      rows.map((x, j) =>
                        j === i ? { ...x, mode: e.target.value } : x,
                      ),
                    )
                  }
                  className="h-10 rounded-xl border border-border bg-white px-3 text-sm flex-1"
                  data-testid={`pay-mode-${i}`}
                >
                  {availableModes.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>

                <Input
                  type="number"
                  value={r.amount}
                  onChange={(e) =>
                    setRows(
                      rows.map((x, j) =>
                        j === i ? { ...x, amount: e.target.value } : x,
                      ),
                    )
                  }
                  className="h-10 w-28 rounded-xl text-right"
                  data-testid={`pay-amount-${i}`}
                />
              </div>
            ))}
          </div>
          {primaryMode === "UPI" && (
            <div className="mt-4 rounded-2xl border border-orange-200 bg-orange-50/60 p-4">
              <div className="flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-orange-500 text-white">
                  <QrCode size={18} />
                </div>

                <div>
                  <p className="text-sm font-bold text-stone-900">
                    Direct UPI Payment
                  </p>
                  <p className="text-xs text-stone-500">
                    Scan this QR using any UPI app
                  </p>
                </div>
              </div>

              {upiId ? (
                <>
                  <div className="mt-4 flex justify-center">
                    <div className="rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(
                          upiQrData,
                        )}`}
                        alt={`UPI payment QR for ${restaurantName}`}
                        className="h-52 w-52 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="mt-3 text-center">
                    <p className="text-sm font-semibold text-stone-700">
                      {restaurantName}
                    </p>

                    <p className="mt-0.5 text-xs text-stone-400">{upiId}</p>
                  </div>

                  <Button
                    type="button"
                    disabled={upiConfirmed}
                    onClick={() => {
                      setUpiConfirmed(true);

                      setRows((currentRows) =>
                        currentRows.map((row, index) =>
                          index === 0
                            ? {
                                ...row,
                                mode: "UPI",
                                amount: due,
                              }
                            : row,
                        ),
                      );

                      toast.success("UPI payment marked as received");
                    }}
                    className={cn(
                      "mt-4 w-full h-11 rounded-xl font-semibold",
                      upiConfirmed
                        ? "bg-emerald-600 hover:bg-emerald-600"
                        : "bg-orange-500 hover:bg-orange-600",
                    )}
                  >
                    {upiConfirmed ? (
                      <>
                        <CheckCircle2 size={17} className="mr-2" />
                        Payment Received
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={17} className="mr-2" />
                        Mark Payment Received
                      </>
                    )}
                  </Button>

                  <div className="mt-3 rounded-xl bg-white px-3 py-2.5 text-xs leading-5 text-stone-500">
                    Ask the customer to complete the payment and verify the
                    payment in the restaurant's UPI app or bank account.
                  </div>
                </>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-stone-300 bg-white p-4 text-center">
                  <QrCode size={26} className="mx-auto text-stone-400" />

                  <p className="mt-2 text-sm font-semibold text-stone-700">
                    UPI is not configured
                  </p>

                  <p className="mt-1 text-xs text-stone-500">
                    Please add the restaurant UPI ID in Settings.
                  </p>
                </div>
              )}
            </div>
          )}
          <div
            className={cn(
              "text-sm text-center font-semibold",
              Math.abs(remaining) > 1 ? "text-amber-600" : "text-emerald-600",
            )}
          >
            {remaining > 1
              ? `${money(remaining)} remaining`
              : remaining < -1
                ? `${money(-remaining)} excess`
                : "Balanced"}
          </div>

          <DialogFooter>
            <Button
              className="w-full h-11 rounded-xl font-semibold"
              onClick={confirm}
              disabled={busy}
            >
              {busy ? (
                <>
                  <Loader2 size={17} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ReceiptText size={17} className="mr-2" />
                  Confirm & Generate Bill
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!receiptOrder}
        onOpenChange={(v) => {
          if (!v) {
            setReceiptOrder(null);
            setOpen(false);
            onDone?.();
          }
        }}
      >
        <DialogContent className="bg-white w-[calc(100vw-24px)] sm:max-w-sm max-h-[90dvh] overflow-y-auto overscroll-contain">
          <DialogHeader>
            <DialogTitle className="font-display">Receipt</DialogTitle>
          </DialogHeader>

          <div
            id="thermal-receipt"
            className="mx-auto w-[300px] bg-white text-black font-mono text-xs"
          >
            <div className="text-center space-y-1">
              <p className="text-base font-bold">
                {restaurantName || "Restaurant"}
              </p>
              {restaurantAddress && (
                <p className="text-center text-xs">{restaurantAddress}</p>
              )}

              {restaurantPhone && (
                <p className="text-center text-xs">Phone: {restaurantPhone}</p>
              )}
              <p>Order #{receiptOrder?.order_no}</p>
              <p>{formatReceiptDateTime(receiptOrder?.created_at)}</p>
            </div>

            <div className="border-t border-dashed my-3" />

            {(receiptOrder?.items || []).map((item, i) => (
              <div key={i} className="flex justify-between gap-2">
                <span>
                  {item.name} ×{item.qty}
                </span>
                <span>{money(item.price * item.qty)}</span>
              </div>
            ))}

            <div className="border-t border-dashed my-3" />

            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{money(receiptOrder?.subtotal || 0)}</span>
            </div>

            {receiptOrder?.discount_total > 0 && (
              <div className="flex justify-between">
                <span>Discount</span>
                <span>-{money(receiptOrder.discount_total)}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span>GST</span>
              <span>{money2(receiptOrder?.tax_amount || 0)}</span>
            </div>

            <div className="border-t border-dashed my-2" />

            <div className="flex justify-between text-sm">
              <span>Gross Total</span>
              <span>{money2(receiptOrder?.total || 0)}</span>
            </div>

            {receiptPaymentDiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span>
                  {receiptPaymentMode} Discount ({receiptDiscountPct}%)
                </span>
                <span className="text-green-600">
                  -{money2(receiptPaymentDiscount)}
                </span>
              </div>
            )}

            <div className="flex justify-between text-sm font-bold">
              <span>Final Amount</span>
              <span>{money(receiptFinalAmount)}</span>
            </div>

            <div className="flex justify-between mt-1">
              <span>Paid</span>
              <span>{money(receiptOrder?.paid_amount || 0)}</span>
            </div>

            <div className="border-t border-dashed my-3" />

            <p className="text-center font-bold">Thank You!</p>

            {restaurantTagline && (
              <p className="text-center text-xs mt-1">{restaurantTagline}</p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:flex-col">
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 rounded-xl font-semibold"
              onClick={() => setWhatsappOpen(true)}
            >
              <MessageCircle size={18} className="mr-2 text-green-600" />
              Share on WhatsApp
            </Button>

            <Button
              className="w-full h-11 rounded-xl font-semibold"
              onClick={printThermalReceipt}
            >
              Print Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={whatsappOpen} onOpenChange={setWhatsappOpen}>
        <DialogContent className="bg-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              Share Bill on WhatsApp
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-stone-700">
                Customer Mobile Number
              </label>

              <Input
                type="tel"
                inputMode="numeric"
                maxLength={13}
                value={whatsappNumber}
                onChange={(e) =>
                  setWhatsappNumber(e.target.value.replace(/[^\d+]/g, ""))
                }
                placeholder="Enter 10-digit mobile number"
                className="mt-2 h-11 rounded-xl"
                autoFocus
              />

              <p className="mt-1.5 text-xs text-stone-500">
                Bill will be shared through WhatsApp.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-xl"
              onClick={() => {
                setWhatsappOpen(false);
                setWhatsappNumber("");
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              className="h-11 rounded-xl font-semibold bg-green-600 hover:bg-green-700"
              onClick={shareBillOnWhatsApp}
            >
              <MessageCircle size={18} className="mr-2" />
              Share on WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
